import {
    type ChangeEvent,
    type FormEvent,
    useCallback,
    useEffect,
    useState,
} from "react";
import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import Select from "react-select";

import type { IUserPermissions } from "Pages/Users/ManageUsers/ManageUsersModal/ManageUsersModal";
import styles from "Pages/Admin/AdminConfiguration/AdminConfigModal/AdminConfigModal.module.css";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import useUserContext from "Hooks/useUserContext";

const groupBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) =>
    arr.reduce((groups, item) => {
        (groups[key(item)] ||= []).push(item);
        return groups;
    }, {} as Record<K, T[]>);

export type ActionType = "new" | "edit" | "delete" | "";
export interface IProps {
    activeModal: ActionType;
    onSetActiveModal: (modal: ActionType) => void;
    onSetSelectedRow: (row: any) => void;
    selectedRow: any;
    integrations: IIntegration[];
    getRoles: () => void;
}

const defaultPermissions = {
    process: "",
    review: "",
    abandon: "",
    admin: "",
    users: "",
    config: "",
    schedule: "",
    connection: "",
    matchTools: "",
};

const defaultFormData = {
    name: "",
    permissions: "",
};

export default function ManageRolesModal({
    activeModal,
    onSetActiveModal,
    onSetSelectedRow,
    selectedRow,
    integrations,
    getRoles,
}: IProps) {
    const [formData, setFormData] = useState(defaultFormData);
    const [permissions, setPermissions] =
        useState<IUserPermissions>(defaultPermissions);

    const [userContext] = useUserContext();
    const { newAlert } = useAlertContext();
    const { handleFormChange } = useHelpers();
    const { handlePermissions } = useHelpers();
    const { post } = useFetch();

    // Set default formData and permissions for edit and delete role
    useEffect(() => {
        if (activeModal === "") {
            return;
        }
        if (activeModal === "new") {
            setPermissions(defaultPermissions);
            setFormData(defaultFormData);
            return;
        }

        let permissions = selectedRow?.values.permissions?.split(
            " | "
        ) as string[];
        let processIndex = permissions?.indexOf(
            permissions.find((v) => v.includes("Process:"))!
        );
        let reviewIndex = permissions?.indexOf(
            permissions.find((v) => v?.includes("Review:"))!
        );
        let process =
            processIndex !== undefined && permissions !== undefined
                ? permissions[processIndex]?.split(":")[1]
                : undefined;
        let review =
            reviewIndex !== undefined && permissions !== undefined
                ? permissions[reviewIndex!]?.split(":")[1]
                : undefined;
        let abandon = selectedRow?.values.permissions.includes("Abandon")
            ? "Abandon"
            : "";
        let admin = selectedRow?.values.permissions.includes("Admin")
            ? "Admin"
            : "";
        let users = selectedRow?.values.permissions.includes("Users")
            ? "Users"
            : "";
        let config = selectedRow?.values.permissions.includes("Config")
            ? "Config"
            : "";
        let schedule = selectedRow?.values.permissions.includes("Schedule")
            ? "Schedule"
            : "";
        let connection = selectedRow?.values.permissions.includes("Connection")
            ? "Connection"
            : "";
        let matchTools = selectedRow?.values.permissions.includes("MatchTools")
            ? "MatchTools"
            : "";

        setPermissions({
            process: process,
            review: review,
            abandon: abandon,
            admin: admin,
            users: users,
            config: config,
            schedule: schedule,
            connection: connection,
            matchTools: matchTools,
        });
        setFormData({
            name: selectedRow?.values.name,
            permissions: "",
        });
    }, [
        activeModal,
        selectedRow?.values.name,
        selectedRow?.values.permissions,
    ]);

    const closeModal = useCallback(() => {
        onSetActiveModal("");
        setFormData(defaultFormData);
        setPermissions(defaultPermissions);
    }, [onSetActiveModal]);

    async function submitFormData(e: FormEvent) {
        e.preventDefault();

        const url = `/api/users/roles/${activeModal}`;
        const body = {
            company_id: userContext.companyId,
            name: formData.name,
            permissions: getPermissionsString(),
        };
        const response = await post(url, body);
        let data;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log(data);

        closeModal();

        if (response.ok) {
            // Show the alert
            newAlert({
                isOpen: true,
                message: "Request submitted successfully",
                color: "success",
            });
            // Re fetch the roles and clear the selected row state
            getRoles();
            onSetSelectedRow(undefined);
        } else {
            newAlert({
                isOpen: true,
                message: `Error, request failed`,
                color: "danger",
            });
        }
    }

    function getPermissionsString() {
        return (
            (permissions?.role
                ? "Role:" + permissions?.role + "[splitMarker]"
                : "") +
            (permissions?.process
                ? "Process:" + permissions?.process + "[splitMarker]"
                : "") +
            (permissions?.review
                ? "Review:" + permissions?.review + "[splitMarker]"
                : "") +
            (permissions?.abandon
                ? permissions?.abandon + "[splitMarker]"
                : "") +
            (permissions?.admin ? permissions?.admin + "[splitMarker]" : "") +
            (permissions?.users ? permissions?.users + "[splitMarker]" : "") +
            (permissions?.config ? permissions?.config + "[splitMarker]" : "") +
            (permissions?.schedule
                ? permissions?.schedule + "[splitMarker]"
                : "") +
            (permissions?.connection
                ? permissions?.connection + "[splitMarker]"
                : "") +
            (permissions?.matchTools ? permissions?.matchTools : "")
        )
            .split("[splitMarker]")
            .filter((x) => x !== "")
            .join(" | ");
    }

    let headerContent = "";
    switch (activeModal) {
        case "new":
            headerContent = "Create New Role";
            break;
        case "edit":
            headerContent = `Edit Role`;
            break;
        case "delete":
            headerContent = `Delete Role`;

            break;
    }

    const processes = groupBy(integrations, (i) => i.integration_group);
    const processOptions = Object.entries(processes)
        .map((integration) => ({
            label: integration[1][0]?.integration_group,
            options: integration[1].map((i) => ({
                value: i.process,
                label: i.name,
            })),
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1));

    function togglePermission(e: ChangeEvent<HTMLInputElement>) {
        const key = e.target.id;
        const keyWithFirstLetterCapitalized =
            key.charAt(0).toUpperCase() + key.slice(1);

        let newPermissions = {};
        if (e.target.checked) {
            newPermissions = {
                ...permissions,
                [key]: keyWithFirstLetterCapitalized,
            };
        } else {
            newPermissions = { ...permissions, [key]: "" };
        }
        setPermissions(newPermissions);
    }

    // Next 2 functions are used to set the default values for the process and review react select multi select inputs
    function getProcessValues() {
        if (permissions?.process) {
            const defaultPermissions = permissions.process
                .split(",")
                .map((r) => ({
                    value: r,
                    label: r,
                }));
            console.log(defaultPermissions);
            return defaultPermissions;
        }
        return [];
    }

    function getReviewValues() {
        if (permissions?.review)
            return permissions.review.split(",").map((r) => ({
                value: r,
                label: r,
            }));
    }

    const bodyContent = (
        <Form id="form" onSubmit={submitFormData}>
            {/* Name */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="name">Name:</Label>
                </Col>
                <Col sm={9}>
                    <Input
                        id="name"
                        name="name"
                        className={styles.input}
                        minLength={3}
                        maxLength={100}
                        required
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.name || ""}
                        disabled={
                            activeModal === "delete" || activeModal === "edit"
                        }
                    />
                </Col>
            </FormGroup>

            {/* Process */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="process">Process:</Label>
                </Col>
                <Col sm={9}>
                    <Select
                        classNamePrefix="rs"
                        options={processOptions}
                        isMulti
                        onChange={(e) =>
                            handlePermissions(e, setPermissions, "Process:")
                        }
                        value={getProcessValues()}
                        isDisabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Review */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="Review">Review:</Label>
                </Col>
                <Col sm={9}>
                    <Select
                        classNamePrefix="rs"
                        options={[
                            { value: "ViewAll", label: "View All" },
                            { value: "RerunAll", label: "Rerun All" },
                        ]}
                        isMulti
                        onChange={(e) =>
                            handlePermissions(e, setPermissions, "Review:")
                        }
                        value={getReviewValues()}
                        isDisabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Checkbox inputs */}
            {/* Abandon */}
            <FormGroup
                row
                className={styles.formGroup}
                // This input is removed and not used. Code can be refactored to remove all references.
                style={{ display: "none" }}
            >
                <Col sm={2}>
                    <Label for="abandon">Abandon:</Label>
                </Col>
                <Col sm={2}>
                    <Input
                        sm={1}
                        id="abandon"
                        name="abandon"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.abandon !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Admin */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="admin">Admin:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="admin"
                        name="admin"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.admin !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Config */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="config">Config:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="config"
                        name="config"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.config !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Connection */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="connection">Connection:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="connection"
                        name="connection"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.connection !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Match Tools */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="matchTools">Match Tools:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="matchTools"
                        name="matchTools"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.matchTools !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Schedule */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="schedule">Schedule:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="schedule"
                        name="schedule"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.schedule !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Users */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="Users">Users:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="users"
                        name="users"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.users !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>
        </Form>
    );

    return (
        <Modal isOpen={activeModal !== ""} toggle={closeModal}>
            <ModalHeader toggle={closeModal}>{headerContent}</ModalHeader>
            <ModalBody>{bodyContent}</ModalBody>
            <ModalFooter id="modal_footer">
                <Button color="primary" type="submit" form="form">
                    Submit
                </Button>
            </ModalFooter>
        </Modal>
    );
}
